<template>
  <span
    class="allocation-group-handle"
    role="button"
    tabindex="0"
    draggable="true"
    title="Drag to move this distribution group to another week"
    aria-label="Drag to move every capacity-type portion of this distribution group to another week"
    @click.stop
    @dragstart="onDragStart"
    @dragend="onDragEnd"
  >
    ⛓
  </span>
</template>

<script setup lang="ts">
  import { useCalendarDragDrop } from '@/composables/useCalendarDragDrop';

  const props = defineProps<{
    distributionGroupId: string;
    // Drag-source identity — only set at week granularity (see CapacityCalendar.vue);
    // undefined for any row/period where a portal vendor isn't linked. Mirrors how
    // CapacityAllocationBar.vue itself types/guards these same values.
    vendorId?: string;
    weekKey?: string;
    memberCapacityTypeIds: string[];
  }>();

  const { startDrag, cancelDrag } = useCalendarDragDrop();

  function onDragStart(event: DragEvent): void {
    if (!props.weekKey || !props.vendorId || !props.distributionGroupId || !props.memberCapacityTypeIds.length) {
      return;
    }

    // Firefox requires setData to be called for a drag to initiate at all;
    // the actual move is resolved from the module-scoped payload, not this data.
    event.dataTransfer?.setData('text/plain', props.distributionGroupId);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
    }
    startDrag({
      type: 'group',
      weekKey: props.weekKey,
      vendorId: props.vendorId,
      distributionGroupId: props.distributionGroupId,
      memberCapacityTypeIds: props.memberCapacityTypeIds,
    });
  }

  function onDragEnd(): void {
    // No-op if a compatible drop already resolved and cleared this — only
    // matters when the drag was cancelled or dropped somewhere invalid.
    cancelDrag();
  }
</script>

<style scoped>
  .allocation-group-handle {
    position: absolute;
    right: 0.25rem;
    top: 0.125rem;
    z-index: 3;
    padding: 0.0625rem 0.25rem;
    border-radius: 0.25rem;
    background: rgb(255 255 255 / 85%);
    color: rgb(71 85 105);
    font-size: 0.625rem;
    line-height: 1;
    cursor: grab;
  }

  .allocation-group-handle:active {
    cursor: grabbing;
  }

  .allocation-group-handle:focus-visible {
    outline: 2px solid rgb(37 99 235);
    outline-offset: 1px;
  }
</style>
